import api from './api';
import { CAB_AGENDA_ELIGIBLE_CHANGE_STATES } from '../constants/cabAgendaOptions';
import { stripHtml } from '../utils/sanitize';
import type {
  CabAgendaItem,
  CreateCabAgendaItemPayload,
  LookupChangeRequest,
  UpdateCabAgendaItemPayload,
} from '../types/cabAgendaItem';

export async function fetchCabAgendaItems(
  meetingId: string,
  params: { page?: number; pageSize?: number; search?: string } = {}
): Promise<{ items: CabAgendaItem[]; total: number }> {
  const response = await api.get<{ items: CabAgendaItem[]; total: number }>(
    `/cab-meetings/${meetingId}/agenda-items`,
    {
      params: {
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 25,
        search: params.search || undefined,
      },
    }
  );
  return response.data;
}

/** Next Order value for a new agenda item (1 if none exist). */
export async function fetchNextCabAgendaSortOrder(meetingId: string): Promise<number> {
  const { items } = await fetchCabAgendaItems(meetingId, { page: 1, pageSize: 500 });
  if (items.length === 0) return 1;
  const maxOrder = items.reduce((max, item) => Math.max(max, item.sortOrder || 0), 0);
  return maxOrder + 1;
}

export async function createCabAgendaItem(
  meetingId: string,
  payload: CreateCabAgendaItemPayload
): Promise<CabAgendaItem> {
  const response = await api.post<CabAgendaItem>(
    `/cab-meetings/${meetingId}/agenda-items`,
    payload
  );
  return response.data;
}

export async function getCabAgendaItemById(
  meetingId: string,
  agendaItemId: string
): Promise<CabAgendaItem> {
  const response = await api.get<CabAgendaItem>(
    `/cab-meetings/${meetingId}/agenda-items/${agendaItemId}`
  );
  return response.data;
}

export async function updateCabAgendaItem(
  meetingId: string,
  agendaItemId: string,
  payload: UpdateCabAgendaItemPayload
): Promise<CabAgendaItem> {
  const response = await api.put<CabAgendaItem>(
    `/cab-meetings/${meetingId}/agenda-items/${agendaItemId}`,
    payload
  );
  return response.data;
}

export async function deleteCabAgendaItem(
  meetingId: string,
  agendaItemId: string
): Promise<void> {
  await api.delete(`/cab-meetings/${meetingId}/agenda-items/${agendaItemId}`);
}

function formatAssignedName(user: {
  firstName?: string;
  lastName?: string;
} | null | undefined): string {
  if (!user) return '—';
  return `${user.firstName || ''} ${user.lastName || ''}`.trim() || '—';
}

export async function fetchChangeRequestsForLookup(params: {
  page: number;
  pageSize: number;
  search?: string;
}): Promise<{ items: LookupChangeRequest[]; total: number }> {
  const offset = (params.page - 1) * params.pageSize;
  const response = await api.get<{ data: any[]; total: number }>('/changes', {
    params: {
      limit: params.pageSize,
      offset,
      search: params.search || undefined,
      changeState: CAB_AGENDA_ELIGIBLE_CHANGE_STATES.join(','),
    },
  });

  const items: LookupChangeRequest[] = (response.data.data || []).map((change) => {
    const assignedTo =
      change.assignedTo || change.User_Case_assignedToIdToUser || null;
    const assignmentGroup =
      change.assignmentGroup || change.Group_Case_assignmentGroupIdToGroup || null;

    return {
      id: change.id,
      number: change.number || '',
      shortDescription: stripHtml(change.shortDescription || ''),
      assignedToName: formatAssignedName(assignedTo),
      assignmentGroupName: assignmentGroup?.name || '—',
      changeState: change.changeState || null,
    };
  });

  return {
    items,
    total: response.data.total ?? items.length,
  };
}
